// The controller wires storage, the channel and the store together, outside React so it can be
// tested with a fake channel. The UI calls it; it never reads the DOM beyond vibration and visibility.
import type { Card, Command, DeviceMessage } from "@whistle/protocol";
import { dbClear, dbDelete, dbDeleteWhere, dbGet, dbGetAll, dbPut, requestPersistentStorage } from "../lib/db";
import { cancelText, startHold, type HoldCancelReason, type HoldHandle } from "../lib/hold";
import { generateDeviceIdentity, loadIdentity, saveIdentity, type DeviceIdentity } from "../lib/identity";
import { answerBlocker, commandEffect, requestSummary, type AnswerEffect, type AppState, type PairingRecord, type Prefs, type PushHealth, type SendRecord } from "../lib/model";
import { PairError } from "../lib/pairing";
import { currentSubscription, enablePush, readPushHealth, registerSubscription, type EnablePushResult } from "../lib/push";
import type { Store } from "../lib/store";
import { RelayChannel, type Channel } from "../lib/transport";

export type Draft = { key: string; requestId: string; promptHash: string; text?: string; answers?: Record<string, string[]>; other?: Record<string, string>; updatedAt: number };
export const draftKey = (requestId: string, promptHash: string) => `${requestId}|${promptHash}`;
/** A draft not touched for this long is deleted (SPEC "Phone storage": 24 hours; security review 2, S2). */
export const DRAFT_MAX_AGE_MS = 24 * 60 * 60_000;

export interface AppStorage {
  loadPairing(): Promise<PairingRecord | null>;
  savePairing(p: PairingRecord | null): Promise<void>;
  loadIdentity(): Promise<DeviceIdentity | null>;
  /** Makes a device identity without storing it. */
  newIdentity(): Promise<DeviceIdentity>;
  saveIdentity(d: DeviceIdentity): Promise<void>;
  loadRecent(): Promise<SendRecord[]>;
  saveRecent(rec: SendRecord): Promise<void>;
  deleteRecent(commandId: string): Promise<void>;
  loadPrefs(): Promise<Partial<Prefs>>;
  savePrefs(p: Prefs): Promise<void>;
  loadDraft(key: string): Promise<Draft | null>;
  saveDraft(d: Draft): Promise<void>;
  deleteDraft(key: string): Promise<void>;
  /** Deletes every draft last saved before `before`. */
  purgeDrafts(before: number): Promise<void>;
  /** The pairing, drafts and Recent. Counters stay: a high-water mark is never lowered (docs/protocol.md, "Envelopes"). */
  clearPairingData(): Promise<void>;
}

export const idbStorage: AppStorage = {
  loadPairing: () => dbGet<PairingRecord>("kv", "pairing").then((p) => p ?? null),
  savePairing: (p) => (p ? dbPut("kv", p, "pairing") : dbDelete("kv", "pairing")),
  loadIdentity,
  newIdentity: () => generateDeviceIdentity(),
  saveIdentity,
  loadRecent: () => dbGetAll<SendRecord>("recent"),
  saveRecent: (rec) => dbPut("recent", { ...rec, id: rec.commandId }),
  deleteRecent: (commandId) => dbDelete("recent", commandId),
  loadPrefs: () => dbGet<Prefs>("kv", "prefs").then((p) => p ?? {}),
  savePrefs: (p) => dbPut("kv", p, "prefs"),
  loadDraft: (key) => dbGet<Draft>("drafts", key).then((d) => d ?? null),
  saveDraft: (d) => dbPut("drafts", d),
  deleteDraft: (key) => dbDelete("drafts", key),
  purgeDrafts: (before) => dbDeleteWhere<Draft>("drafts", (d) => !(d.updatedAt >= before)),
  clearPairingData: async () => {
    await dbDelete("kv", "pairing");
    await dbClear("drafts");
    await dbClear("recent");
  },
};

/** In-memory storage for mock mode and tests: nothing from a fake pairing ever reaches IndexedDB. */
export function memoryStorage(seed: { pairing?: PairingRecord | null; identity?: DeviceIdentity | null; recent?: SendRecord[]; prefs?: Partial<Prefs>; drafts?: Draft[] } = {}): AppStorage & { drafts: Map<string, Draft>; recent: Map<string, SendRecord> } {
  let pairing = seed.pairing ?? null;
  let identity = seed.identity ?? null;
  let prefs: Partial<Prefs> = seed.prefs ?? {};
  const drafts = new Map<string, Draft>((seed.drafts ?? []).map((d) => [d.key, d]));
  const recent = new Map<string, SendRecord>((seed.recent ?? []).map((r) => [r.commandId, r]));
  return {
    drafts,
    recent,
    loadPairing: async () => pairing,
    savePairing: async (p) => void (pairing = p),
    loadIdentity: async () => identity,
    newIdentity: () => generateDeviceIdentity(),
    saveIdentity: async (d) => void (identity = d),
    loadRecent: async () => [...recent.values()],
    saveRecent: async (rec) => void recent.set(rec.commandId, rec),
    deleteRecent: async (commandId) => void recent.delete(commandId),
    loadPrefs: async () => prefs,
    savePrefs: async (p) => void (prefs = p),
    loadDraft: async (key) => drafts.get(key) ?? null,
    saveDraft: async (d) => void drafts.set(d.key, d),
    deleteDraft: async (key) => void drafts.delete(key),
    purgeDrafts: async (before) => {
      for (const [key, d] of drafts) if (!(d.updatedAt >= before)) drafts.delete(key);
    },
    clearPairingData: async () => {
      pairing = null;
      drafts.clear();
      recent.clear();
    },
  };
}

export type HoldView = { requestId: string; agentName: string; summary: string; effect: AnswerEffect; remainingMs: number; totalMs: number } | null;

/** A one-value store for the hold banner, separate from the app state so 50 ms ticks don't re-render the page. */
export class Signal<T> {
  private value: T;
  private readonly fns = new Set<() => void>();
  constructor(v: T) {
    this.value = v;
  }
  get = (): T => this.value;
  set(v: T): void {
    this.value = v;
    for (const fn of this.fns) fn();
  }
  subscribe = (fn: () => void): (() => void) => {
    this.fns.add(fn);
    return () => this.fns.delete(fn);
  };
}

export type ControllerOptions = {
  store: Store;
  storage: AppStorage;
  /** Makes the channel for a pairing. Mock mode supplies the fake daemon; identity is null there. */
  channelFactory: (pairing: PairingRecord, identity: DeviceIdentity | null) => Channel;
  now?: () => number;
  vibrate?: (pattern: number | number[]) => void;
  pushHealth?: () => Promise<PushHealth>;
  enablePush?: typeof enablePush;
  online?: () => boolean;
  /** How long a command may sit without any result before the app asks for state again. */
  checkAfterMs?: number;
  /** How long with no result at all before the send is marked unknown. */
  unknownAfterMs?: number;
  /** How long turning alerts on waits for the relay connection before saying it isn't connected. */
  connectWaitMs?: number;
  /** Mock mode only: pairings are fake records, completed without a device identity of their own. */
  fakePairing?: boolean;
};

export class Controller {
  readonly store: Store;
  readonly hold = new Signal<HoldView>(null);
  private readonly o: ControllerOptions;
  private channel: Channel | null = null;
  private identity: DeviceIdentity | null = null;
  /** Fresh identities made for pairings in progress, by deviceId. Each is stored only when its own pairing completes. */
  private readonly pendingIdentities = new Map<string, DeviceIdentity>();
  private unsubs: (() => void)[] = [];
  private holdHandle: HoldHandle | null = null;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  // StrictMode mounts, destroys and boots again before the first boot's reads finish. Each async step checks its
  // generation after every await, so a superseded boot or connect stops instead of opening a second relay socket.
  private booting: Promise<void> | null = null;
  private bootGen = 0;
  private connectGen = 0;

  constructor(o: ControllerOptions) {
    this.o = o;
    this.store = o.store;
  }

  private now(): number {
    return this.o.now?.() ?? Date.now();
  }

  get isConnected(): boolean {
    return !!this.channel?.connected;
  }

  // ---------- Lifecycle ----------

  /** Idempotent until destroy(): a second call returns the first boot. */
  boot(): Promise<void> {
    this.booting ??= this.start(this.bootGen);
    return this.booting;
  }

  private async start(gen: number): Promise<void> {
    const [pairing, recent, prefs, push] = await Promise.all([this.o.storage.loadPairing(), this.o.storage.loadRecent(), this.o.storage.loadPrefs(), this.o.pushHealth ? this.o.pushHealth() : readPushHealth()]);
    if (gen !== this.bootGen) return;
    const online = this.o.online ? this.o.online() : typeof navigator !== "undefined" && typeof navigator.onLine === "boolean" ? navigator.onLine : true;
    const at = this.now();
    this.store.dispatch({ type: "boot", pairing, recent, prefs, push, online, at });
    // What boot dropped from Recent, or stripped of its typed text, leaves IndexedDB too (security review 2, S2).
    const kept = this.store.getState().sends;
    for (const rec of recent) {
      const k = kept[rec.commandId];
      if (!k) void this.o.storage.deleteRecent(rec.commandId);
      else if (rec.text !== undefined && k.text === undefined) void this.o.storage.saveRecent(k);
    }
    this.lastPersisted.sends = kept;
    void this.o.storage.purgeDrafts(at - DRAFT_MAX_AGE_MS);
    this.unsubs.push(this.store.subscribe((s) => this.persist(s)));
    if (typeof window !== "undefined") {
      const onOnline = () => this.store.dispatch({ type: "online", online: true });
      const onOffline = () => this.store.dispatch({ type: "online", online: false });
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", onOffline);
      this.unsubs.push(() => {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
      });
    }
    if (pairing) {
      void requestPersistentStorage();
      await this.connect(pairing);
    }
  }

  async connect(pairing: PairingRecord): Promise<void> {
    this.disconnect();
    const gen = this.connectGen;
    const identity = await this.o.storage.loadIdentity();
    if (gen !== this.connectGen) return;
    this.identity = identity;
    const ch = this.o.channelFactory(pairing, this.identity);
    this.channel = ch;
    const off = [
      ch.on("status", (status) => this.store.dispatch({ type: "conn", status })),
      ch.on("ready", (members) => {
        this.store.dispatch({ type: "ready", members, at: this.now() });
        void ch.send({ type: "hello" });
        void this.reRegisterPush();
      }),
      ch.on("presence", (p) => this.store.dispatch({ type: "presence", id: p.id, online: p.online, lastSeen: p.lastSeen })),
      ch.on("message", (msg) => this.store.dispatch({ type: "daemon", msg, at: this.now() })),
      ch.on("removed", () => this.store.dispatch({ type: "removed" })),
      ch.on("pushResult", (r) => {
        if (r.gone) this.store.dispatch({ type: "push", push: { registered: false, subscribed: false, lastError: "The push service dropped this subscription." } });
      }),
    ];
    this.unsubs.push(...off);
    ch.start();
  }

  disconnect(): void {
    this.connectGen++;
    this.channel?.stop();
    this.channel = null;
  }

  destroy(): void {
    this.bootGen++;
    this.booting = null;
    this.cancelHold("user");
    this.disconnect();
    for (const u of this.unsubs) u();
    this.unsubs = [];
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
  }

  /** Ask the machine for a fresh snapshot (after a notification, on return to the app, when unsure). */
  refresh(): void {
    void this.channel?.send({ type: "hello" });
  }

  private lastPersisted: { sends?: AppState["sends"]; prefs?: Prefs; removed?: boolean; wiped?: boolean } = {};
  private persist(s: AppState): void {
    if (s.removed !== !!this.lastPersisted.removed) {
      this.lastPersisted.removed = s.removed;
      this.lastPersisted.wiped = false;
      if (s.removed) {
        // Recent emptied on screen by the removal is not pruned from storage: only the machine's own word deletes it.
        this.lastPersisted.sends = s.sends;
        this.cancelHold("disconnected");
        this.disconnect();
      }
    }
    if (s.removedByMachine && !this.lastPersisted.wiped) {
      this.lastPersisted.wiped = true;
      this.wipeAfterRemoval();
    }
    if (s.sends !== this.lastPersisted.sends) {
      const prev = this.lastPersisted.sends ?? {};
      for (const rec of Object.values(s.sends)) if (prev[rec.commandId] !== rec) void this.o.storage.saveRecent(rec);
      // Pruned in memory means gone from IndexedDB as well.
      for (const id of Object.keys(prev)) if (!(id in s.sends)) void this.o.storage.deleteRecent(id);
      this.lastPersisted.sends = s.sends;
    }
    if (s.prefs !== this.lastPersisted.prefs) {
      this.lastPersisted.prefs = s.prefs;
      void this.o.storage.savePrefs(s.prefs);
    }
  }

  /**
   * The machine's signed notice named this phone as removed: nothing typed or shown for it stays here (SPEC "Phone
   * storage"; security review 2, S2). Counters and prefs stay. The relay's word alone wipes nothing, so a phone removed
   * while offline keeps its Recent and drafts until they age out (24 hours for text) or the owner taps "Forget this
   * pairing": the price of a relay that can't un-pair it.
   */
  private wipeAfterRemoval(): void {
    void this.o.storage.clearPairingData();
  }

  // ---------- Answering (SPEC 5.4) ----------

  /**
   * Start the 2-second hold for an answer. `build` makes the Command at the moment of sending, with the
   * stamp from the state on screen then. `effect` is what the tapped choice does, for the hold banner; the record takes
   * its effect from the command that is built. Returns false if the card can't be answered right now.
   */
  answer(card: Card, build: () => Command, summary: string, text?: string, effect: AnswerEffect = "answer"): boolean {
    const state = this.store.getState();
    if (answerBlocker(state, card, this.now())) return false;
    if (!this.isConnected) {
      // The state still says connected but the socket is gone (the browser can kill it without a close event, as the
      // back-forward cache does). Say so on the card rather than holding for two seconds and failing, and reconnect.
      this.store.dispatch({ type: "notSent", requestId: card.requestId, reason: cancelText("disconnected"), at: this.now() });
      this.channel?.reconnect();
      return false;
    }
    this.cancelHold("user");
    this.store.dispatch({ type: "clearNotSent", requestId: card.requestId });
    const channel = this.channel;
    const mockHook = (channel as { onHoldStarted?: (id: string) => void } | null)?.onHoldStarted;
    mockHook?.call(channel, card.requestId);
    const total = 2000;
    this.vibrate(12);
    const unsubChange = this.store.subscribe((s) => {
      const live = s.cards.find((c) => c.requestId === card.requestId);
      if (!live || live.promptHash !== card.promptHash) this.holdHandle?.cancel("changed");
      else if (answerBlocker(s, card, this.now())?.code === "paused" || s.removed) this.holdHandle?.cancel("disconnected");
    });
    const handle = startHold({
      ms: total,
      now: () => this.now(),
      isConnected: () => !!channel?.connected && this.store.getState().machineOnline !== false,
      onTick: (remaining) => this.hold.set({ requestId: card.requestId, agentName: card.agentName, summary, effect, remainingMs: remaining, totalMs: total }),
      onFire: () => {
        unsubChange();
        this.hold.set(null);
        this.holdHandle = null;
        void this.sendNow(card, build, summary, text);
      },
      onCancel: (reason: HoldCancelReason) => {
        unsubChange();
        this.hold.set(null);
        this.holdHandle = null;
        this.store.dispatch({ type: "notSent", requestId: card.requestId, reason: cancelText(reason), at: this.now() });
      },
    });
    this.holdHandle = handle;
    return true;
  }

  cancelHold(reason: HoldCancelReason = "user"): void {
    this.holdHandle?.cancel(reason);
    this.holdHandle = null;
  }

  private async sendNow(card: Card, build: () => Command, summary: string, text?: string): Promise<void> {
    const state = this.store.getState();
    const live = state.cards.find((c) => c.requestId === card.requestId);
    const blocker = live ? answerBlocker(state, live, this.now()) : null;
    if (!live || live.promptHash !== card.promptHash) return this.store.dispatch({ type: "notSent", requestId: card.requestId, reason: cancelText("changed"), at: this.now() });
    if (blocker) return this.store.dispatch({ type: "notSent", requestId: card.requestId, reason: blocker.code === "disconnected" ? cancelText("disconnected") : `Not sent. ${blocker.text}`, at: this.now() });
    let command: Command;
    try {
      command = build();
    } catch (e) {
      return this.store.dispatch({ type: "notSent", requestId: card.requestId, reason: `Not sent. ${e instanceof Error ? e.message : "The answer couldn't be built."}`, at: this.now() });
    }
    const machineName = state.machine?.name ?? state.pairing?.machine.name ?? "your PC";
    const record: SendRecord = {
      commandId: command.commandId,
      requestId: card.requestId,
      promptHash: card.promptHash,
      agentName: card.agentName,
      agentKind: card.agentKind,
      machineName,
      envLabel: card.envLabel,
      project: card.project,
      summary,
      effect: commandEffect(command, card),
      title: card.title,
      request: requestSummary(card),
      text: text?.trim() ? text : undefined,
      startedAt: this.now(),
      updatedAt: this.now(),
      stage: "sending",
    };
    this.store.dispatch({ type: "sendStarted", record });
    this.vibrate([20, 40, 20]);
    const msg: DeviceMessage = { type: "command", command };
    const ok = await (this.channel?.send(msg) ?? Promise.resolve(false));
    if (!ok) {
      this.store.dispatch({ type: "sendStage", commandId: command.commandId, stage: "not-sent", reason: cancelText("disconnected"), at: this.now() });
      return;
    }
    if (text?.trim()) void this.o.storage.deleteDraft(draftKey(card.requestId, card.promptHash));
    // Never resend. After a while with no word, ask for state; after longer, say so honestly.
    this.after(this.o.checkAfterMs ?? 8000, () => {
      if (this.store.getState().sends[command.commandId]?.stage === "sending") this.refresh();
    });
    this.after(this.o.unknownAfterMs ?? 45_000, () => {
      if (this.store.getState().sends[command.commandId]?.stage === "sending")
        this.store.dispatch({ type: "sendStage", commandId: command.commandId, stage: "unknown", reason: `No reply from ${machineName}.`, at: this.now() });
    });
  }

  // ---------- Settings ----------

  setAlerts(mode: "auto" | "away" | "quiet", until?: number): void {
    void this.channel?.send({ type: "setAlerts", mode, until });
  }

  setRemote(paused: boolean): void {
    void this.channel?.send({ type: "setRemote", paused });
  }

  setPrefs(prefs: Partial<Prefs>): void {
    this.store.dispatch({ type: "prefs", prefs });
  }

  // ---------- Push ----------

  async enablePush(): Promise<EnablePushResult> {
    const pairing = this.store.getState().pairing;
    const fn = this.o.enablePush ?? enablePush;
    const run = () => fn({ relayOrigin: pairing?.relayOrigin ?? "", sendFrame: (f) => this.channel?.sendFrame(f) ?? false });
    let result = await run();
    // Right after pairing the relay connection is often still coming up. Subscribing worked, so wait for the
    // connection and register then (permission is already granted, so this asks nothing of the user).
    if (!result.ok && result.why === "not-connected" && (await this.waitForConnection(this.o.connectWaitMs ?? 10_000))) result = await run();
    this.store.dispatch({ type: "push", push: result.health });
    return result;
  }

  /** Resolves true once the channel is connected, false after `ms` (or straight away when there is no pairing). */
  private waitForConnection(ms: number): Promise<boolean> {
    if (this.isConnected) return Promise.resolve(true);
    if (!this.store.getState().pairing) return Promise.resolve(false);
    return new Promise((resolve) => {
      const done = (ok: boolean) => {
        clearTimeout(timer);
        unsub();
        resolve(ok);
      };
      const timer = setTimeout(() => done(false), ms);
      const unsub = this.store.subscribe((s) => {
        if (s.conn === "connected" && this.isConnected) done(true);
      });
    });
  }

  async refreshPushHealth(): Promise<void> {
    const health = this.o.pushHealth ? await this.o.pushHealth() : await readPushHealth(this.store.getState().push.registered);
    this.store.dispatch({ type: "push", push: health });
  }

  private async reRegisterPush(): Promise<void> {
    if (this.o.pushHealth) return; // tests and mock mode
    const sub = await currentSubscription();
    if (sub && this.channel) {
      const sent = registerSubscription(sub, (f) => this.channel?.sendFrame(f) ?? false);
      this.store.dispatch({ type: "push", push: { subscribed: true, registered: sent } });
    }
  }

  // ---------- Pairing ----------

  async completePairing(record: PairingRecord): Promise<void> {
    const fresh = this.pendingIdentities.get(record.deviceId);
    // Saved, a pairing whose key this phone doesn't hold could never connect, and it would replace one that can.
    if (!fresh && !this.o.fakePairing) throw new PairError("bad-response", "This phone doesn't hold the key for that pairing, so it was not saved. Scan the code again.");
    this.pendingIdentities.delete(record.deviceId);
    // The identity first: the service worker reads the pairing and the identity together.
    if (fresh) await this.o.storage.saveIdentity(fresh);
    await this.o.storage.savePairing(record);
    void requestPersistentStorage();
    this.store.dispatch({ type: "paired", pairing: record });
    await this.connect(record);
  }

  /**
   * A new device identity for every pairing (security review 2, S26). The daemon pairs a device key only once, so an
   * envelope from an earlier pairing stays a replay there (docs/protocol.md, "Rules the daemon enforces"), and one
   * sealed to an old key can't be opened with the new one here. It is stored only when the pairing completes, so a
   * cancelled pairing leaves the current one working.
   */
  async newPairingIdentity(): Promise<DeviceIdentity> {
    const identity = await this.o.storage.newIdentity();
    this.pendingIdentities.set(identity.deviceId, identity);
    return identity;
  }

  async unpair(): Promise<void> {
    this.cancelHold("user");
    this.disconnect();
    await this.o.storage.clearPairingData();
    this.store.dispatch({ type: "unpaired" });
  }

  // ---------- Drafts (SPEC 5.5: kept until sent, for up to DRAFT_MAX_AGE_MS untouched) ----------

  async loadDraft(card: Pick<Card, "requestId" | "promptHash">): Promise<Draft | null> {
    const key = draftKey(card.requestId, card.promptHash);
    const d = await this.o.storage.loadDraft(key);
    if (d && !(this.now() - d.updatedAt < DRAFT_MAX_AGE_MS)) {
      void this.o.storage.deleteDraft(key);
      return null;
    }
    return d;
  }

  saveDraft(card: Pick<Card, "requestId" | "promptHash">, d: Omit<Draft, "key" | "requestId" | "promptHash" | "updatedAt">): Promise<void> {
    if (this.store.getState().removed) return Promise.resolve();
    const empty = !d.text?.trim() && !Object.values(d.answers ?? {}).some((a) => a.length) && !Object.values(d.other ?? {}).some((t) => t.trim());
    const key = draftKey(card.requestId, card.promptHash);
    return empty ? this.o.storage.deleteDraft(key) : this.o.storage.saveDraft({ key, requestId: card.requestId, promptHash: card.promptHash, ...d, updatedAt: this.now() });
  }

  // ---------- Helpers ----------

  private vibrate(pattern: number | number[]): void {
    if (!this.store.getState().prefs.haptics) return;
    try {
      (this.o.vibrate ?? ((p) => navigator.vibrate?.(p)))(pattern);
    } catch {
      /* unsupported */
    }
  }

  private after(ms: number, fn: () => void): void {
    const t = setTimeout(() => {
      this.timers.delete(t);
      fn();
    }, ms);
    this.timers.add(t);
  }
}

/** The real channel factory: envelopes over the relay. */
export function relayChannelFactory(pairing: PairingRecord, identity: DeviceIdentity | null): Channel {
  if (!identity) throw new Error("no device identity");
  return new RelayChannel({ pairing, identity });
}
